import { DynamoDB, DynamoDBClientConfig } from "@aws-sdk/client-dynamodb";
import {
  BatchGetCommand,
  BatchGetCommandInput,
  BatchWriteCommand,
  BatchWriteCommandInput,
  DeleteCommand,
  DeleteCommandInput,
  DynamoDBDocumentClient,
  GetCommand,
  GetCommandInput,
  PutCommand,
  PutCommandInput,
  QueryCommand,
  QueryCommandInput,
  ScanCommand,
  ScanCommandInput,
  TransactGetCommand,
  TransactGetCommandInput,
  TransactWriteCommand,
  TransactWriteCommandInput,
  UpdateCommand,
  UpdateCommandInput,
} from "@aws-sdk/lib-dynamodb";
import { marshall, unmarshall } from "@aws-sdk/util-dynamodb";

import { DynamoDBEventName, DynamoDBEventRecord } from "@examspark/types";

export const eventRecordsFactory = (...records: any) => {
  return {
    Records: records,
  };
};

export const eventRecordFactory = (
  eventName: DynamoDBEventName,
  tableName: string,
  data: {
    NewImage: any;
    OldImage: any; // caution: odd ddb marshalling error if undefined.
  },
): any => {
  const image = data.NewImage.id ? data.NewImage : data.OldImage;
  const record = {
    eventID: "eb2321743f6719a41ce145d5f83ae1be",
    eventName: eventName,
    eventVersion: "1.1",
    eventSource: "aws:dynamodb",
    awsRegion: "us-east-1",
    dynamodb: {
      ApproximateCreationDateTime: 1572459528,
      SequenceNumber: "4815300000000003582363157",
      SizeBytes: 117,
      StreamViewType: "NEW_AND_OLD_IMAGES",
      Keys: marshall({
        id: image.id,
        sk: image.sk,
      }),
      NewImage: marshall(data.NewImage),
      OldImage: marshall(data.OldImage),
    },
    eventSourceARN: generateEventSourceARN(tableName),
  } as DynamoDBEventRecord;

  if (data.NewImage && record.eventName !== "REMOVE") {
    record.dynamodb.NewImage = marshall(data.NewImage);
  }
  if (data.OldImage && record.eventName !== "INSERT") {
    record.dynamodb.OldImage = marshall(data.OldImage);
  }

  return record;
};

export const generateEventSourceARN = (tableName: string) => {
  return [
    "arn",
    "aws",
    "dynamodb",
    "us-east-1",
    "722656715617",
    `table/${tableName}/stream/2019-10-29T17:36:25.676`,
  ].join(":");
};

export const getImagesFromEventRecord = (record: DynamoDBEventRecord) => {
  switch (record.eventName) {
    case "INSERT":
      return {
        newImage: unmarshall(record.dynamodb.NewImage),
        oldImage: undefined,
      };
    case "REMOVE":
      return {
        newImage: undefined,
        oldImage: unmarshall(record.dynamodb.OldImage),
      };
    case "MODIFY":
      return {
        newImage: unmarshall(record.dynamodb.NewImage),
        oldImage: unmarshall(record.dynamodb.OldImage),
      };
  }
};

export class DocumentClient extends DynamoDBDocumentClient {
  public readonly client: DynamoDB;

  constructor(clientOrConfig?: DynamoDB | DynamoDBClientConfig) {
    let client: DynamoDB;
    if (clientOrConfig instanceof DynamoDB) {
      client = clientOrConfig;
    } else if (clientOrConfig != null) {
      client = new DynamoDB(clientOrConfig);
    } else {
      client = new DynamoDB();
    }

    super(client, { marshallOptions: { removeUndefinedValues: true } });

    this.client = client;
  }

  batchGet(input: BatchGetCommandInput) {
    return this.send(new BatchGetCommand(input));
  }
  batchWrite(input: BatchWriteCommandInput) {
    return this.send(new BatchWriteCommand(input));
  }
  delete(input: DeleteCommandInput) {
    return this.send(new DeleteCommand(input));
  }
  get(input: GetCommandInput) {
    return this.send(new GetCommand(input));
  }
  put(input: PutCommandInput) {
    return this.send(new PutCommand(input));
  }
  query(input: QueryCommandInput) {
    return this.send(new QueryCommand(input));
  }
  scan(input: ScanCommandInput) {
    return this.send(new ScanCommand(input));
  }
  update(input: UpdateCommandInput) {
    return this.send(new UpdateCommand(input));
  }
  transactGet(input: TransactGetCommandInput) {
    return this.send(new TransactGetCommand(input));
  }
  transactWrite(input: TransactWriteCommandInput) {
    return this.send(new TransactWriteCommand(input));
  }
}

export type DocumentKey = NonNullable<GetCommandInput["Key"]>;
